import { useNavigate, useParams } from "@tanstack/react-router";
import { type Participant, type Raid, type Resource } from "@/raids/rest/RaidSchema.ts";
import { Button, message, Modal, Skeleton, Tag, Tooltip } from "antd";
import { PageHead } from "@/components/PageHead.tsx";
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { PageScrollable } from "@/layout/PageScrollable.tsx";
import { RaidOverView } from "@/raids/raidDetail/RaidOverView.tsx";
import { RaidResourcePanel } from "@/raids/raidDetail/RaidResourcePanel.tsx";
import { RaidParticipant } from "@/raids/raidDetail/RaidParticipant.tsx";
import {
  useGetRaid,
  useStartRaid,
  useStopRaid,
  useUpdateRaid,
} from "@/raids/rest/RaidQueries.ts";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getPinForRaid } from "@/raids/pinStore.ts";
import { RaidEditTour } from "@/raids/tour/RaidEditTour.tsx";

export function RaidEditDetails() {
  const { raidId: raidIdParam } = useParams({ from: "/raids_/$raidId/edit" });
  const raidId = useMemo(() => Number(raidIdParam), [raidIdParam]);
  const isValidRaidId = Number.isFinite(raidId) && raidId > 0;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [msgApi, contextHolder] = message.useMessage();

  const navigateBack = useCallback(() => {
    void navigate({ to: "/raids" });
  }, [navigate]);

  const navigateToRead = useCallback(() => {
    void navigate({
      to: "/raids/$raidId",
      params: { raidId: String(raidId) },
    });
  }, [navigate, raidId]);

  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const focusRestoreTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isValidRaidId) {
      void msgApi.error("Ungültige Raid-ID");
      navigateBack();
    }
  }, [isValidRaidId, msgApi, navigateBack]);

  const {
    data: raid,
    isLoading,
    isError,
    error,
  } = useGetRaid(raidId, isValidRaidId, false);

  const [currentRaid, setCurrentRaid] = useState<Raid | null>(null);

  useEffect(() => {
    setCurrentRaid(null);
  }, [raidId]);

  /** Nur initial aus GET füllen — nie danach `raid` blind übernehmen, sonst nach PATCH ein alter Cache die frische `version` aus onSuccess überschreibt → 409 beim nächsten Speichern. */
  useEffect(() => {
    if (!raid) return;
    setCurrentRaid((prev) => (prev === null ? raid : prev));
  }, [raid]);

  const displayRaid = currentRaid ?? raid ?? null;

  useEffect(() => {
    if (!focusedInput) return;
    if (focusRestoreTimeoutRef.current) {
      clearTimeout(focusRestoreTimeoutRef.current);
    }
    focusRestoreTimeoutRef.current = window.setTimeout(() => {
      const element = document.querySelector(
        `[data-focus-key="${focusedInput}"]`,
      ) as HTMLElement;
      if (element) {
        element.focus();
      }
      setFocusedInput(null);
      focusRestoreTimeoutRef.current = null;
    }, 0);

    return () => {
      if (focusRestoreTimeoutRef.current) {
        clearTimeout(focusRestoreTimeoutRef.current);
      }
    };
  }, [focusedInput, currentRaid]);

  const applyRaidMutationResult = useCallback(
    (next: Raid, successMsg: string) => {
      setCurrentRaid(next);
      queryClient.setQueryData(["raid", raidId], next);
      void msgApi.success(successMsg);
    },
    [queryClient, raidId, msgApi],
  );

  const handleTimeTrackChanged = useCallback(
    (updatedRaid: Raid) => {
      setCurrentRaid(updatedRaid);
      queryClient.setQueryData(["raid", raidId], updatedRaid);
    },
    [queryClient, raidId],
  );

  const updateRaidMutation = useUpdateRaid({
    onSuccess: (next) => {
      applyRaidMutationResult(next, "Raid gespeichert");
    },
    onError: (status, data) => {
      const snap = queryClient.getQueryData<Raid>(["raid", raidId]);
      if (snap) setCurrentRaid(snap);
      if (status === 409) {
        void Modal.confirm({
          title: "Daten veraltet",
          content:
            "Dieser Raid wurde inzwischen geändert. Zur Detailansicht wechseln und neu laden?",
          okText: "Zur Ansicht",
          cancelText: "Abbrechen",
          onOk: () => {
            void queryClient.invalidateQueries({ queryKey: ["raid", raidId] });
            navigateToRead();
          },
        });
        return;
      }
      const detail =
          typeof data === "string"
              ? data
              : data != null
                  ? JSON.stringify(data)
                  : "";
      void msgApi.error(
          "Speichern fehlgeschlagen: " + (detail || "Unbekannter Fehler"),
      );
    },
  });

  const startRaidMutation = useStartRaid({
    onSuccess: (next) => {
      applyRaidMutationResult(next, "Raid gestartet");
    },
    onError: (status, data) => {
      const detail = typeof data === "string" ? data : "";
      void msgApi.error(
        "Starten des Raids fehlgeschlagen: " +
          (detail || `Status ${String(status ?? "?")}`),
      );
    },
  });

  const stopRaidMutation = useStopRaid({
    onSuccess: (next) => {
      applyRaidMutationResult(next, "Raid beendet");
    },
    onError: (status, data) => {
      const detail = typeof data === "string" ? data : "";
      void msgApi.error(
        "Beenden des Raids fehlgeschlagen: " +
          (detail || `Status ${String(status ?? "?")}`),
      );
    },
  });

  useEffect(() => {
    if (isError) {
      void msgApi.error(
        "Laden des Raids fehlgeschlagen. " + (error as AxiosError)?.message,
      );
      navigateBack();
    }
  }, [isError, error, msgApi, navigateBack]);

  /** Optimistic-Locking: Backend vergleicht Body-`version` mit DB. `currentRaid` kann hinter `setQueryData` hängen — immer die zuletzt bekannte Server-Version aus dem Query-Cache mitsenden. */
  const applyLatestKnownVersion = useCallback(
    (draft: Raid): Raid => {
      const cached = queryClient.getQueryData<Raid>(["raid", raidId]);
      if (cached == null) return draft;
      return { ...draft, version: cached.version };
    },
    [queryClient, raidId],
  );

  function updateRaidName(changedRaidName: string) {
    if (!displayRaid || updateRaidMutation.isPending) return;
    const toUpdate = applyLatestKnownVersion({
      ...displayRaid,
      name: changedRaidName,
    });
    setCurrentRaid(toUpdate);
    updateRaidMutation.mutate({ id: raidId, payload: toUpdate });
  }

  function handleStartRaid() {
    if (!displayRaid) return;
    startRaidMutation.mutate({
      id: raidId,
      payload: applyLatestKnownVersion(displayRaid),
    });
  }

  function handleStopRaid() {
    if (!displayRaid) return;
    stopRaidMutation.mutate({
      id: raidId,
      payload: applyLatestKnownVersion(displayRaid),
    });
  }

  function handleAddParticipant(participant: Participant) {
    if (!displayRaid || updateRaidMutation.isPending) return;
    const toUpdate = applyLatestKnownVersion({
      ...displayRaid,
      participants: [...(displayRaid.participants ?? []), participant],
    });
    setCurrentRaid(toUpdate);
    updateRaidMutation.mutate({ id: raidId, payload: toUpdate });
  }

  function handleRemoveParticipant(id: Participant["id"]) {
    if (!displayRaid || updateRaidMutation.isPending) return;

    const toUpdate = applyLatestKnownVersion({
      ...displayRaid,
      participants: displayRaid.participants.filter((p) => p.id !== id),
    });
    setCurrentRaid(toUpdate);
    updateRaidMutation.mutate({ id: raidId, payload: toUpdate });
  }

  function handleUpdateResources(resources: Resource[]) {
    if (!displayRaid || updateRaidMutation.isPending) return;
    const toUpdate = applyLatestKnownVersion({
      ...displayRaid,
      resources: resources,
    });
    setCurrentRaid(toUpdate);
    updateRaidMutation.mutate({ id: raidId, payload: toUpdate });
  }

  function handleFocusInput(inputKey: string) {
    setFocusedInput(inputKey);
  }

  function handleEndEdit() {
    navigateToRead();
  }

  const isSaving =
    updateRaidMutation.isPending ||
    startRaidMutation.isPending ||
    stopRaidMutation.isPending;
  const isEditAble = displayRaid !== null;
  const storedPin = getPinForRaid(raidId);

  if (!isValidRaidId) return null;

  const startCommand = (
    <>
      {!isLoading && displayRaid?.status === "CREATED" && (
        <div data-tour="raid-start-btn">
          <Tooltip title={"Raid starten"}>
            <Button icon={<PlayCircleOutlined />} onClick={handleStartRaid}>
              Starten
            </Button>
          </Tooltip>
        </div>
      )}
    </>
  );

  const closeCommand = (
    <>
      {!isLoading && displayRaid?.status === "RUNNING" && (
        <div data-tour="raid-stop-btn">
          <Tooltip title={"Raid beenden"}>
            <Button icon={<StopOutlined />} onClick={handleStopRaid}>
              Beenden
            </Button>
          </Tooltip>
        </div>
      )}
    </>
  );

  const closedTag = (
    <>
      {!isLoading && displayRaid?.status === "CLOSED" && (
        <div>
          <Tooltip title={"Raid beendet"}>
            <Tag color="volcano" className={"m-w-28! m-h-"}>
              Beendet
            </Tag>
          </Tooltip>
        </div>
      )}
    </>
  );

  const cmdBar = (
    <div className={"flex flex-wrap justify-end items-center gap-2"}>
      {isLoading && (
        <Skeleton.Button active shape="round" className={"m-w-28!"} />
      )}
      {startCommand}
      {closeCommand}
      {closedTag}
      {!isLoading && (
        <Button onClick={handleEndEdit}>Bearbeitung beenden</Button>
      )}
    </div>
  );

  return (
    <PageScrollable
      title={
        <PageHead
          title="Raid bearbeiten"
          rightComponent={cmdBar}
          leftComponent={
            <Tooltip title={"Zurück"}>
              <Button icon={<ArrowLeftOutlined />} onClick={navigateBack} />
            </Tooltip>
          }
        />
      }
    >
      <div
        className={
          "flex w-full max-w-full flex-col gap-2 max-md:h-auto max-md:flex-none md:min-h-0 md:flex-1"
        }
      >
        {contextHolder}
        <div className={"flex w-full shrink-0 flex-col gap-2"}>
          <RaidOverView
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={isSaving}
            isEditAble={isEditAble}
            callBackSaveName={updateRaidName}
            editPin={storedPin}
          />
        </div>
        <div
          className={
            "flex w-full min-w-0 max-w-full flex-col gap-2 duration-300 max-md:h-auto max-md:flex-none md:min-h-0 md:flex-1 md:flex-row"
          }
        >
          <RaidResourcePanel
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={isSaving}
            isEditAble={isEditAble}
            callBackUpdateResources={handleUpdateResources}
            onFocusInput={handleFocusInput}
          />
          <RaidParticipant
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={isSaving}
            isEditAble={isEditAble}
            callBackAddParticipant={handleAddParticipant}
            callBackRemoveParticipant={handleRemoveParticipant}
            onFocusInput={handleFocusInput}
            onTimeTrackChanged={handleTimeTrackChanged}
          />
        </div>
      </div>
      <RaidEditTour raidStatus={displayRaid?.status} />
    </PageScrollable>
  );
}
